import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { authClient } from '../lib/auth-client';
import { apiFetch } from '../lib/api';
import { consumePendingRedirect } from '../lib/post-auth-redirect';
import { captureDemoRef, readDemoRef, withDemoRef } from '../lib/demo-ref';
import { useT } from '../locales/context';

export function SignIn() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const justReset = searchParams.get('reset') === '1';
  // Playground-signup-card cycle (THI-33, 2026-08-22 spec §B) — a visitor following the
  // playground's sign-up CTA who already has an account lands here instead; capture `?demo=`
  // the same way SignUp.tsx does so attribution still fires after this sign-in.
  useEffect(() => {
    captureDemoRef(window.location.search);
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: providers } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () =>
      apiFetch<{ emailPassword: boolean; google: boolean }>('/api/meta/auth-providers'),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const { error: signInError } = await authClient.signIn.email({ email, password });
    setIsSubmitting(false);
    if (signInError) {
      if (signInError.status === 403) {
        setError(t.auth.unverifiedResendNotice);
      } else {
        setError(signInError.message ?? t.auth.genericFormError);
      }
      return;
    }
    // Hard navigation, NOT `navigate('/')` (2026-08-20): the SPA route change raced
    // `useSession`'s store — Protected could mount while the hook still cached the
    // pre-sign-in `null` session (isPending false) and silently bounce back here with no
    // error; the in-flight get-session showed as (canceled) in devtools. Same cache-reset
    // rationale as admin-impersonate.ts's `location.assign` across a session boundary.
    //
    // workspace-members cycle (THI-110 Task 5) — `/invite/:token` (InviteAccept.tsx) stashes
    // its own path here before sending an unauthenticated visitor to sign in, so accepting an
    // invite survives the sign-in round trip instead of landing on the dashboard root with the
    // invite forgotten (see lib/post-auth-redirect.ts's own doc comment for why sessionStorage
    // rather than a `?next=` this page reads itself).
    location.assign(consumePendingRedirect() ?? '/');
  }

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t.auth.signInTitle}</CardTitle>
            <CardDescription>{t.auth.signInSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            {justReset && !error && (
              <p className="mb-4 text-sm" style={{ color: 'var(--st-ready)' }}>
                {t.auth.passwordJustResetNotice}
              </p>
            )}
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">{t.auth.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t.auth.passwordLabel}</Label>
                  <Link to="/forgot-password" className="text-xs text-muted-foreground underline">
                    {t.auth.forgotPasswordLink}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.auth.signingIn : t.auth.signInCta}
              </Button>
              {providers?.google && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    authClient.signIn.social({
                      provider: 'google',
                      callbackURL: withDemoRef('/', readDemoRef()),
                    })
                  }
                >
                  {t.auth.continueWithGoogle}
                </Button>
              )}
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t.auth.noAccountPrompt}
              <Link to="/sign-up" className="underline">
                {t.auth.signUpLink}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
